import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useIsDarkMode } from "../../../../theme/ThemeContext";
import { StatTile } from "../../../../components/UI/States";
import {
  STATUS,
  customerLabel,
  daysActive,
  isOpen,
} from "../../../../components/WindowDoor/ServiceRequests/constants";

/**
 * Office-facing reporting over the full request history (the dashboard
 * already loads every request, so everything here is computed client-side).
 *
 * Average-days-to-complete only counts requests finished in THIS system:
 * imported rows carry their submission date as their status date, so
 * including them would report zero-day completions.
 */

const MONTHS_SHOWN = 24;

const monthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const monthLabel = (key) => {
  const [year, month] = key.split("-");
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString([], {
    month: "short",
    year: "2-digit",
  });
};

const csvEscape = (value) => {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const exportRequestsCsv = (rows, filename) => {
  const columns = [
    ["Entry", (row) => row.entry_number],
    ["Submitted", (row) => new Date(row.entry_date).toLocaleString()],
    ["Status", (row) => row.status],
    ["Customer", (row) => customerLabel(row)],
    ["Phone", (row) => row.customer_phone],
    ["Address", (row) => row.address],
    ["City", (row) => row.city],
    ["State", (row) => row.state],
    ["Zip", (row) => row.zip],
    ["Contractor", (row) => row.contractor],
    ["Salesperson", (row) => row.salesperson],
    ["Salesperson Email", (row) => row.salesperson_email],
    ["Manufacturer", (row) => row.manufacturer],
    ["Assignee", (row) => row.assigned_to?.name],
    ["Days Active", (row) => daysActive(row)],
    ["Order Number", (row) => row.checklist?.order_number],
    ["Issue", (row) => row.describe_issue],
  ];
  const lines = [
    columns.map(([header]) => csvEscape(header)).join(","),
    ...rows.map((row) =>
      columns.map(([, value]) => csvEscape(value(row))).join(",")
    ),
  ];
  // BOM so Excel opens it as UTF-8.
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const Kpi = ({ label, value, hint, tone }) => (
  <StatTile label={label} value={value} hint={hint} tone={tone} />
);

const BreakdownBar = ({ label, count, total }) => (
  <div className="flex items-center gap-3 text-sm">
    <span className="w-32 shrink-0 truncate sm:w-36">{label}</span>
    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-400/20">
      <div
        className="h-full rounded-full bg-sky-500"
        style={{ width: `${total ? Math.max(2, (count / total) * 100) : 0}%` }}
      />
    </div>
    <span className="w-14 shrink-0 text-right font-semibold tabular-nums">
      {count.toLocaleString()}
    </span>
  </div>
);

const ServiceRequestReports = ({ requests }) => {
  const isDark = useIsDarkMode();
  const rows = useMemo(
    () => (requests || []).filter((row) => row.deleted !== 1),
    [requests]
  );

  const stats = useMemo(() => {
    const now = new Date();
    const thisYear = now.getFullYear();

    const open = rows.filter((row) => isOpen(row.status));
    const completedThisYear = rows.filter(
      (row) =>
        row.status === STATUS.COMPLETED &&
        new Date(row.status_updated_at).getFullYear() === thisYear
    );

    // Completions measurable in this system only (see module comment).
    const measurable = rows.filter(
      (row) =>
        row.status === STATUS.COMPLETED &&
        !row.imported &&
        row.status_updated_at &&
        new Date(row.status_updated_at) > new Date(row.entry_date)
    );
    const avgDays = measurable.length
      ? Math.round(
          measurable.reduce(
            (sum, row) =>
              sum +
              (new Date(row.status_updated_at) - new Date(row.entry_date)) /
                86400000,
            0
          ) / measurable.length
        )
      : null;

    // Monthly received volume, last MONTHS_SHOWN months.
    const months = [];
    for (let i = MONTHS_SHOWN - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: monthKey(date), label: monthLabel(monthKey(date)), received: 0 });
    }
    const byMonth = new Map(months.map((month) => [month.key, month]));
    rows.forEach((row) => {
      const date = new Date(row.entry_date);
      if (Number.isNaN(date.getTime())) return;
      const bucket = byMonth.get(monthKey(date));
      if (bucket) bucket.received++;
    });

    // Top salespeople: total submitted + how many still open.
    const bySales = new Map();
    rows.forEach((row) => {
      const name = (row.salesperson || "Unknown").trim() || "Unknown";
      const entry = bySales.get(name.toLowerCase()) || { name, total: 0, open: 0 };
      entry.total++;
      if (isOpen(row.status)) entry.open++;
      bySales.set(name.toLowerCase(), entry);
    });
    const topSales = [...bySales.values()]
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);

    // Manufacturer breakdown. History predates the field, so bucket blanks.
    const byMake = new Map();
    rows.forEach((row) => {
      const make = (row.manufacturer || "").trim() || "Not recorded";
      byMake.set(make, (byMake.get(make) || 0) + 1);
    });
    const makes = [...byMake.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);

    return {
      open: open.length,
      completedThisYear: completedThisYear.length,
      avgDays,
      measurableCount: measurable.length,
      months,
      topSales,
      makes,
    };
  }, [rows]);

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Kpi label="All requests" value={rows.length.toLocaleString()} />
        <Kpi label="Open now" value={stats.open} tone="amber" />
        <Kpi
          label="Completed this year"
          value={stats.completedThisYear}
          tone="emerald"
        />
        <Kpi
          label="Avg days to complete"
          value={stats.avgDays ?? "—"}
          hint={
            stats.avgDays === null
              ? "Measurable once requests complete in Big C Node."
              : `Across ${stats.measurableCount} request${
                  stats.measurableCount === 1 ? "" : "s"
                } completed in Big C Node.`
          }
        />
      </div>

      {/* Monthly volume */}
      <div className="app-card rounded-2xl border p-5">
        <h3 className="mb-4 font-semibold">Requests received per month</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.months} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isDark ? "#33415566" : "#e2e8f0"}
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                interval={2}
              />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: isDark ? "#33415544" : "#f1f5f9" }}
                contentStyle={{
                  backgroundColor: "var(--app-popover-bg)",
                  border: "1px solid var(--app-popover-border)",
                  borderRadius: 12,
                  boxShadow: "var(--app-popover-shadow)",
                  color: "var(--app-text)",
                  fontSize: 12,
                }}
                labelStyle={{ color: "var(--app-text)", fontWeight: 600 }}
                itemStyle={{ color: "var(--app-text)" }}
                formatter={(value) => [value, "Received"]}
              />
              <Bar dataKey="received" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {/* Top salespeople */}
        <div className="app-card overflow-hidden rounded-2xl border">
          <h3 className="px-5 pb-3 pt-5 font-semibold">
            Top submitters (all time)
          </h3>
          <div className="overflow-x-auto">
          <table className="app-table">
            <thead>
              <tr>
                <th className="!pl-5">Salesperson</th>
                <th className="!text-right">Requests</th>
                <th className="!pr-5 !text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {stats.topSales.map((entry) => (
                <tr key={entry.name}>
                  <td className="!pl-5">{entry.name}</td>
                  <td className="text-right font-semibold tabular-nums">
                    {entry.total.toLocaleString()}
                  </td>
                  <td className="!pr-5 text-right tabular-nums">
                    {entry.open > 0 ? (
                      <span className="font-semibold text-amber-600 dark:text-amber-400">
                        {entry.open}
                      </span>
                    ) : (
                      <span className="app-muted">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {/* Manufacturer breakdown */}
        <div className="app-card rounded-2xl border p-5">
          <h3 className="mb-1 font-semibold">By manufacturer</h3>
          <p className="app-muted mb-4 text-xs">
            The old system didn't record manufacturers, so history shows as
            "Not recorded".
          </p>
          <div className="space-y-2.5">
            {stats.makes.slice(0, 10).map((make) => (
              <BreakdownBar
                key={make.label}
                label={make.label}
                count={make.count}
                total={rows.length}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestReports;
